import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import API_URL from '@/config/api';
import { LEAD_SOURCE_LABEL } from '@/config/leadSources';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  TrendingUp,
  Users,
  CheckCircle2,
  PoundSterling,
  Briefcase,
  FileText,
  Trophy,
  Sparkles,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from 'recharts';

const SECTION_COLORS = {
  probound: '#2F5E96',
  proepoxy: '#7A0B7A',
  prodecor: '#D5AB09',
  protraining: '#E0671F',
};

const STATUS_ORDER = ['new', 'contacted', 'booked_in', 'quoted', 'won', 'lost'];
const STATUS_LABEL = {
  new: 'New',
  contacted: 'Contacted',
  booked_in: 'Booked In',
  quoted: 'Quoted',
  won: 'Won',
  lost: 'Lost',
};

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);

const KpiCard = ({ icon: Icon, label, value, accent = 'bg-[#2F5E96]', sub }) => (
  <Card data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`} className="p-5 flex items-start gap-4 hover:shadow-lg transition-shadow">
    <div className={`${accent} text-white p-3 rounded-xl shrink-0`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1 truncate">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  </Card>
);

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const isStaff = user?.role && ['admin', 'manager', 'team'].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/overview?days=${days}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const funnelData = STATUS_ORDER.map((s) => ({
    name: STATUS_LABEL[s],
    count: data?.leads?.by_status?.[s] || 0,
  }));

  const sectionData = data
    ? Object.entries(data.leads.by_section).map(([k, v]) => ({
        name: k,
        count: v,
        fill: SECTION_COLORS[k] || '#6B7280',
      }))
    : [];

  // Marketing attribution — bucket the lead-source counts, sort biggest first,
  // human-label the keys, and compute a percentage of total for the bar widths.
  const sourceTotal = data?.leads?.by_source
    ? Object.values(data.leads.by_source).reduce((a, b) => a + b, 0)
    : 0;
  const sourceData = data?.leads?.by_source
    ? Object.entries(data.leads.by_source)
        .filter(([_, count]) => count > 0)
        .map(([key, count]) => ({
          key,
          label: key === 'unknown' ? 'Not captured' : (LEAD_SOURCE_LABEL[key] || key),
          count,
          pct: sourceTotal > 0 ? (count / sourceTotal) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count)
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#27292E] to-[#3a3d45] text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <button
                data-testid="analytics-back-btn"
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold">Analytics</h1>
                  <span
                    data-testid="analytics-pro-badge"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-yellow-500 text-yellow-950"
                  >
                    <Sparkles className="w-3 h-3" />
                    Business
                  </span>
                </div>
                <p className="text-white/70 text-sm mt-1">
                  {data?.scope === 'company' ? 'Company-wide performance' : 'Your personal performance'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {[30, 90, 365].map((d) => (
                <button
                  key={d}
                  data-testid={`analytics-range-${d}`}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    days === d ? 'bg-white text-[#27292E]' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {d === 365 ? '12mo' : `${d}d`}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading && !data ? (
          <div data-testid="analytics-loading" className="text-center py-20 text-gray-500">
            Loading analytics…
          </div>
        ) : !data ? (
          <div className="text-center py-20 text-gray-500">No data available.</div>
        ) : (
          <div className="space-y-6">
            {/* KPI ROW */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <KpiCard
                icon={Users}
                label="Total Leads"
                value={data.leads.total}
                accent="bg-[#2F5E96]"
                sub={`${data.leads.won} won (${data.leads.conversion_rate}%)`}
              />
              <KpiCard
                icon={TrendingUp}
                label="Conversion"
                value={`${data.leads.conversion_rate}%`}
                accent="bg-emerald-600"
                sub="Won ÷ total leads"
              />
              <KpiCard
                icon={FileText}
                label="Quote £"
                value={fmtGBP(data.quotes.project_quote_value)}
                accent="bg-[#7A0B7A]"
                sub={`${data.quotes.project_quotes} project quotes`}
              />
              <KpiCard
                icon={Briefcase}
                label="Active Projects"
                value={data.projects.active}
                accent="bg-[#E0671F]"
                sub={`Avg ${fmtGBP(data.projects.avg_value)}`}
              />
              <KpiCard
                icon={PoundSterling}
                label="Outstanding"
                value={fmtGBP(data.projects.outstanding)}
                accent="bg-rose-600"
                sub={`Paid ${fmtGBP(data.projects.total_paid)}`}
              />
            </div>

            {/* TREND + FUNNEL */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card data-testid="analytics-trend-card" className="p-5 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Lead Trend</h3>
                  <span className="text-xs text-gray-500">Last {days} days</span>
                </div>
                {data.leads.trend.length === 0 ? (
                  <div className="text-center text-sm text-gray-400 py-10">No leads in this period.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={data.leads.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#2F5E96" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Card>

              <Card data-testid="analytics-funnel-card" className="p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Lead Funnel</h3>
                <div className="space-y-2">
                  {funnelData.map((row) => {
                    const max = Math.max(...funnelData.map((r) => r.count), 1);
                    const pct = (row.count / max) * 100;
                    return (
                      <div key={row.name} data-testid={`funnel-row-${row.name.toLowerCase().replace(/\s+/g, '-')}`}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-600">{row.name}</span>
                          <span className="font-semibold text-gray-900">{row.count}</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#2F5E96] to-[#7A0B7A] transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            {/* SECTION BREAKDOWN + LEAD SOURCES */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card data-testid="analytics-section-card" className="p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Leads by Section</h3>
                {sectionData.length === 0 ? (
                  <div className="text-center text-sm text-gray-400 py-10">No section data.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={sectionData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {sectionData.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>

              <Card data-testid="analytics-sources-card" className="p-5 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Lead Sources</h3>
                  <span className="text-xs text-gray-500">
                    {isStaff ? 'Where customers came from this period' : 'Where your customers came from this period'}
                  </span>
                </div>
                {sourceData.length === 0 ? (
                  <div className="text-center text-sm text-gray-400 py-10">
                    No source data yet — start asking <em>&ldquo;out of curiosity, how did you hear about us?&rdquo;</em> on every call.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sourceData.map((s) => (
                      <div key={s.key} data-testid={`source-row-${s.key}`} className="flex items-center gap-3">
                        <div className="w-44 shrink-0 text-sm text-gray-700 truncate">{s.label}</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${s.pct}%`,
                              background: s.key === 'unknown' ? '#cbd5e1' : '#7A0B7A',
                            }}
                          />
                        </div>
                        <div className="w-20 shrink-0 text-right text-sm tabular-nums">
                          <span className="font-semibold text-gray-900">{s.count}</span>
                          <span className="text-gray-400 ml-1.5 text-xs">{Math.round(s.pct)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* LEADERBOARD (staff only) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

              {isStaff && (
                <Card data-testid="analytics-leaderboard-card" className="p-5 lg:col-span-2">
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    <h3 className="font-semibold text-gray-900">Top Installers</h3>
                  </div>
                  {data.leaderboard.length === 0 ? (
                    <div className="text-center text-sm text-gray-400 py-10">No installer activity yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                            <th className="py-2 pr-2">#</th>
                            <th className="py-2 pr-2">Installer</th>
                            <th className="py-2 pr-2 text-right">Leads</th>
                            <th className="py-2 pr-2 text-right">Won</th>
                            <th className="py-2 pr-2 text-right">Project £</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.leaderboard.map((row, i) => (
                            <tr
                              key={row.user_id}
                              data-testid={`leaderboard-row-${i}`}
                              className="border-b last:border-0 hover:bg-gray-50"
                            >
                              <td className="py-2 pr-2 font-semibold text-gray-400">{i + 1}</td>
                              <td className="py-2 pr-2">
                                <div className="font-medium text-gray-900">{row.name}</div>
                                {row.company_name && (
                                  <div className="text-xs text-gray-500">{row.company_name}</div>
                                )}
                              </td>
                              <td className="py-2 pr-2 text-right">{row.lead_count}</td>
                              <td className="py-2 pr-2 text-right text-emerald-700 font-medium">
                                {row.won_count}
                              </td>
                              <td className="py-2 pr-2 text-right font-semibold">
                                {fmtGBP(row.project_value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              )}

              {!isStaff && (
                <Card data-testid="analytics-personal-card" className="p-5 lg:col-span-2">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold text-gray-900">Quote Performance</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider">Acceptance Rate</div>
                      <div className="text-3xl font-bold text-gray-900 mt-1">
                        {data.quotes.acceptance_rate}%
                      </div>
                      <div className="text-xs text-gray-500">
                        {data.quotes.accepted} of {data.quotes.project_quotes} accepted
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider">Material Quotes</div>
                      <div className="text-3xl font-bold text-gray-900 mt-1">{data.quotes.material_quotes}</div>
                      <div className="text-xs text-gray-500">In this period</div>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            <div className="text-center pt-4">
              <Button
                data-testid="analytics-refresh-btn"
                onClick={load}
                variant="outline"
                className="text-sm"
              >
                Refresh
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
